import { useState, useEffect } from "react";
import Logo from "@shared/Logo";

export const WAITING_STATUSES = [
  "pending_host_confirmation",
  "paid_pending_confirmation",
];

export const TERMINAL_WAIT_STATUSES = [
  "confirmed",
  "failed_unavailable",
  "timeout_unconfirmed",
  "cancelled",
  "failed",
];

export function isWaitingForHost(status?: string | null) {
  return !!status && WAITING_STATUSES.includes(status);
}

export function useWaitWindowCountdown(sessionExpiresAt?: string) {
  const [timeLeft, setTimeLeft] = useState(0);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!sessionExpiresAt) {
      return;
    }

    const expiryDate = new Date(sessionExpiresAt);
    const tick = () => {
      const diffSeconds = Math.floor(
        (expiryDate.getTime() - Date.now()) / 1000,
      );
      if (diffSeconds <= 0) {
        setExpired(true);
        setTimeLeft(0);
        return false;
      }
      setExpired(false);
      setTimeLeft(diffSeconds);
      return true;
    };

    if (!tick()) {
      return;
    }

    const interval = setInterval(() => {
      if (!tick()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionExpiresAt]);

  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const seconds = String(timeLeft % 60).padStart(2, "0");

  return {
    timeLeft,
    expired,
    formattedTime: `${minutes}:${seconds}`,
    minutes,
    seconds,
  };
}

export const ClockIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8.68688 20.8329C7.65604 20.3867 6.75729 19.7803 5.99062 19.0136C5.22396 18.247 4.61754 17.3482 4.17138 16.3174C3.72521 15.2867 3.50213 14.1816 3.50213 13.0021C3.50213 11.8226 3.72521 10.7175 4.17138 9.68688C4.61754 8.65604 5.22396 7.75729 5.99062 6.99063C6.75729 6.22396 7.65604 5.61754 8.68688 5.17138C9.71754 4.72521 10.8226 4.50213 12.0021 4.50213C13.1816 4.50213 14.2867 4.72521 15.3174 5.17138C16.3482 5.61754 17.247 6.22396 18.0136 6.99063C18.7803 7.75729 19.3867 8.65604 19.8329 9.68688C20.279 10.7175 20.5021 11.8226 20.5021 13.0021C20.5021 14.1816 20.279 15.2867 19.8329 16.3174C19.3867 17.3482 18.7803 18.247 18.0136 19.0136C17.247 19.7803 16.3482 20.3867 15.3174 20.8329C14.2867 21.279 13.1816 21.5021 12.0021 21.5021C10.8226 21.5021 9.71754 21.279 8.68688 20.8329ZM14.9751 17.0291L16.0291 15.9751L12.7521 12.6984V8.00213H11.2521V13.3059L14.9751 17.0291ZM5.75587 2.89062L6.80988 3.94438L2.94438 7.80988L1.89062 6.75588L5.75587 2.89062ZM18.2484 2.89062L22.1136 6.75588L21.0599 7.80988L17.1944 3.94438L18.2484 2.89062ZM12.0021 20.0021C13.9458 20.0021 15.5984 19.3214 16.9599 17.9599C18.3214 16.5984 19.0021 14.9458 19.0021 13.0021C19.0021 11.0585 18.3214 9.40588 16.9599 8.04438C15.5984 6.68288 13.9458 6.00213 12.0021 6.00213C10.0585 6.00213 8.40587 6.68288 7.04437 8.04438C5.68287 9.40588 5.00213 11.0585 5.00213 13.0021C5.00213 14.9458 5.68287 16.5984 7.04437 17.9599C8.40587 19.3214 10.0585 20.0021 12.0021 20.0021Z"
      fill="currentColor"
      fillOpacity="0.8"
    />
  </svg>
);

interface WaitWindowCountdownProps {
  sessionExpiresAt?: string;
  compact?: boolean;
}

export function WaitWindowCountdownCompact({
  sessionExpiresAt,
}: WaitWindowCountdownProps) {
  const { formattedTime, expired } = useWaitWindowCountdown(sessionExpiresAt);

  return (
    <span
      className={`wait-window-banner__timer ${expired ? "is-expired" : ""}`}
      aria-live="polite"
    >
      {expired ? "00:00" : formattedTime}
    </span>
  );
}

export default function WaitWindowCountdown({
  sessionExpiresAt,
}: WaitWindowCountdownProps) {
  const { formattedTime, expired } = useWaitWindowCountdown(sessionExpiresAt);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          padding: "30px",
          width: "100%",
          maxWidth: "600px",
        }}
        className="border border-gray-200 rounded-4"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "24px",
          }}
        >
          <Logo width="80px" />
        </div>

        <h3
          style={{
            margin: "0 0 8px",
            fontSize: "22px",
            fontWeight: "700",
            color: "#111827",
            letterSpacing: "-0.01em",
          }}
        >
          Waiting for your host
        </h3>

        <p
          style={{
            margin: "0 0 36px",
            fontSize: "14px",
            color: "#6B7280",
            lineHeight: "1.5",
            fontWeight: "400",
          }}
        >
          Your payment went through. Your host has until the timer runs out to
          accept. We&apos;ll update this page as soon as they respond.
        </p>

        <div
          style={{
            background: "#F0FAFA",
            border: "1.5px solid #1ABCB0",
            borderRadius: "14px",
            padding: "28px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <ClockIcon />
          <span
            style={{
              fontSize: "40px",
              fontWeight: "700",
              color: expired ? "#EF4444" : "#111827",
              letterSpacing: "0.04em",
              fontVariantNumeric: "tabular-nums",
              transition: "color 0.3s ease",
            }}
            aria-live="polite"
          >
            {expired ? "00:00" : formattedTime}
          </span>
        </div>

        {expired && (
          <p
            style={{
              marginTop: "16px",
              textAlign: "center",
              color: "#EF4444",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            The wait window has ended. We&apos;re checking for an update from
            your host.
          </p>
        )}
      </div>
    </div>
  );
}
